import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Download, ExternalLink } from './icons';
import { downloadEvidenceViaAPI, createEvidenceObjectUrl } from '../utils/evidenceProxy';

export default function EvidenceModal({ isOpen, onClose, evidenceUrl, evidenceName = 'evidencia' }) {
    const [imageUrl, setImageUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!isOpen || !evidenceUrl) {
            setImageUrl(null);
            setError(null);
            return;
        }

        const loadImage = async () => {
            try {
                setLoading(true);
                setError(null);
                const objectUrl = await createEvidenceObjectUrl(evidenceUrl);
                setImageUrl(objectUrl);
            } catch (err) {
                console.error('Error al cargar evidencia:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        loadImage();

        // Cleanup
        return () => {
            if (imageUrl) {
                URL.revokeObjectURL(imageUrl);
            }
        };
    }, [isOpen, evidenceUrl]);

    if (!isOpen || !evidenceUrl) return null;

    const handleDownload = () => {
        downloadEvidenceViaAPI(evidenceUrl, evidenceName);
    };

    const handleOpenInNewTab = () => {
        if (imageUrl) {
            window.open(imageUrl, '_blank');
        }
    };

    return createPortal(
        <div
            className="fixed inset-0 bg-black bg-opacity-75 z-[9999] flex items-center justify-center p-4"
            onClick={(e) => {
                if (e.target === e.currentTarget) {
                    onClose();
                }
            }}
        >
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl max-h-[90vh] w-full">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {evidenceName}
                    </h3>
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={handleDownload}
                            className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:text-blue-400 dark:hover:text-blue-300 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                            title="Descargar"
                        >
                            <Download className="w-5 h-5" />
                        </button>
                        <button
                            onClick={handleOpenInNewTab}
                            className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 dark:text-green-400 dark:hover:text-green-300 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                            title="Abrir en nueva pestaña"
                        >
                            <ExternalLink className="w-5 h-5" />
                        </button>
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-700 rounded-lg transition-colors"
                            title="Cerrar"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-4 flex-1 overflow-hidden">
                    {loading && (
                        <div className="flex items-center justify-center h-64">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                    )}

                    {error && (
                        <div className="flex items-center justify-center h-64">
                            <div className="text-center">
                                <div className="text-6xl mb-4">📷</div>
                                <p className="text-red-600 dark:text-red-400 mb-2 text-lg font-semibold">Error al cargar la evidencia</p>
                                <p className="text-sm text-gray-600 dark:text-gray-400">No se pudo cargar la imagen. Verifica que el archivo exista.</p>
                                <p className="text-xs mt-2 text-red-500 dark:text-red-400">{error}</p>
                            </div>
                        </div>
                    )}

                    {imageUrl && !loading && !error && (
                        <div className="flex justify-center">
                            <img
                                src={imageUrl}
                                alt={evidenceName}
                                className="max-w-full max-h-[60vh] object-contain rounded-lg shadow-lg"
                                style={{ maxHeight: '60vh' }}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
}

